import { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { HeroUIProvider } from '@heroui/react';
import { PostHogProvider } from '@posthog/react';
import posthog from 'posthog-js';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import { NotificationManager } from '@components/common/NotificationManager';
import { PaymentNotificationListener } from '@components/common/PaymentNotificationListener';
import { SuspenseLoading } from '@components/common/SuspenseLoading';
import { ErrorBoundary } from '@components/common/ErrorBoundary';
import { TranslationCompatible } from '@components/common/TranslationCompatible';
import { PostHogUserSync } from '@components/common/PostHogUserSync';
import './index.css';
import { ServiceWorkerManager } from '@components/common/ServiceWorkerManager.tsx';
import { suppressFirebasePermissionErrors } from '@lib/utils/firebaseErrorHandler';
import { captureFprFromUrl } from '@lib/utils/referralStorage';

// Initialize PostHog synchronously so __loaded=true before any React effects run,
// preventing identify() and capture() calls in child effects from being dropped.
// Guard against empty key: init('') aborts initialization and leaves __loaded=false.
if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com',
  });
}

// Suppress Firebase permission errors from being logged as errors
suppressFirebasePermissionErrors();

// Rapid deploys purge old hashed chunks; a client still holding a stale
// index.html (tab left open / SW cache) then 404s on its next lazy-route
// import and strands the user on the router error page ("cosmic" screen).
// Vite fires `vite:preloadError` for exactly this — reload once to pick up
// the fresh deploy. `preventDefault` stops the failed import from also
// rejecting into the route error boundary during the reload.
window.addEventListener('vite:preloadError', event => {
  event.preventDefault();
  window.location.reload();
});

// Capture FirstPromoter referral code from URL (?fpr=xxx) into localStorage
// Runs as early as possible so any landing page entry is captured
captureFprFromUrl();

// (No UTM capture here any more — channel attribution is GA4's, see
// src/lib/analytics/trackEvent.ts. The FirstPromoter capture above is unrelated
// and stays.)

// Import development helpers in development mode
if (process.env.NODE_ENV === 'development') {
  import('./dev-helpers');
}

// Reset app state on page load for fresh detection
window.addEventListener('load', () => {
  // Clear app loaded flag to allow fresh detection on next navigation
  sessionStorage.removeItem('app-has-loaded');
  // Reset global onboarding check flag on page load
  (window as any).globalOnboardingChecked = false;
});

// Global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  event.preventDefault();
});

// Global error handler
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  // React.StrictMode intentionally double-invokes effects in development
  // We keep it for catching side effects but have added loading protection in stores
  <PostHogProvider client={posthog}>
    <HeroUIProvider>
      <ErrorBoundary>
        <TranslationCompatible />
        <Suspense fallback={<SuspenseLoading />}>
          <PostHogUserSync />
          <RouterProvider router={router} />
          <NotificationManager />
          <ServiceWorkerManager />
          <PaymentNotificationListener />
        </Suspense>
      </ErrorBoundary>
    </HeroUIProvider>
  </PostHogProvider>
);
